import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Trigger a comparison generation using Anthropic Claude for a single report
 * section. Calls GenerateDraftWithLLM with compare_mode=true, which:
 *   - Forces all paragraphs to regenerate via Anthropic (skipping Gemini)
 *   - Skips all Firestore writes (outputs, inputs, approvals untouched)
 *   - Returns the Claude-generated paragraph texts directly
 *
 * This is a dev/staging-only UAT feature for clinicians to visually compare
 * Gemini vs Claude paragraph outputs side-by-side. The backend hard-rejects
 * compare_mode in production projects regardless of what the client sends.
 *
 * @param {Object} params
 * @param {string} params.patientId - The patient document ID
 * @param {string} params.reportStage - e.g. "Clinical Interview", "Semi-Structured Interview"
 * @param {string} params.reportSection - e.g. "Communication", "Social Interaction"
 * @param {Array<string>} [params.conditions] - Diagnostic conditions (if applicable)
 * @returns {Promise<Object>} Map of { paragraphKey: generatedText } from Claude
 */
export async function triggerCompareWithClaude({ patientId, reportStage, reportSection, conditions = [] }) {
  const user = auth.currentUser;
  if (!user || !user.uid) {
    throw new Error('User not authenticated');
  }

  const GenerateDraftWithLLM = httpsCallable(functions, 'GenerateDraftWithLLM', {
    timeout: 300000,
  });

  const result = await GenerateDraftWithLLM({
    clinicianId: user.uid,
    patientId,
    reportStage,
    reportSection,
    isTest: false,
    conditions,
    compare_mode: true,
  });

  return result.data;
}

/**
 * Trigger a comparison generation for the Semble MDT Report using Anthropic.
 * Calls generateSembleMdtReport with compare_mode=true, which skips Firestore
 * writes and returns the raw Claude-generated paragraph texts directly.
 *
 * @param {Object} params
 * @param {string} params.patientId - The patient document ID
 * @returns {Promise<Object>} Map of { paragraphKey: generatedText } from Claude
 */
export async function triggerCompareWithClaudeSemble({ patientId }) {
  const user = auth.currentUser;
  if (!user || !user.uid) {
    throw new Error('User not authenticated');
  }

  const generateSembleMdtReport = httpsCallable(functions, 'generateSembleMdtReport', {
    timeout: 360000,
  });

  const result = await generateSembleMdtReport({
    clinicianId: user.uid,
    patientId,
    compare_mode: true,
  });

  return result.data;
}
